import { useAtomValue } from 'jotai';
import { RefObject } from 'react';
// stylesheet import type now handled by `src/types/css.d.ts`
import { Alert, Box } from '@optiaxiom/react';
import {
  Connection,
  ConnectionMode,
  Edge,
  EdgeTypes,
  IsValidConnection,
  MiniMap,
  Node,
  NodeTypes,
  OnEdgesChange,
  OnNodesChange,
  ReactFlow,
  ViewportPortal,
} from '@xyflow/react';

import { themeAtom } from '@frontend/atoms';

import { CustomConnectionLine, CustomEdge } from '../edges';
import { WorkflowValidationError } from '../types/node-validation';

import { styles } from './WorkflowCanvas.css';

import '@xyflow/react/dist/style.css';

const edgeTypes: EdgeTypes = {
  custom: CustomEdge,
};

export function WorkflowCanvas({
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  onNodeClick,
  onConnect,
  isValidConnection,
  onNodesDelete,
  onEdgesDelete,
  handleDragOver,
  handleDrop,
  nodeTypes,
  reactFlowWrapper,
  fitView = false,
  showErrors = false,
  workflowErrors = [],
}: {
  nodes: Node[];
  edges: Edge[];
  onNodesChange: OnNodesChange;
  onEdgesChange: OnEdgesChange;
  onNodeClick?: (event: React.MouseEvent, node: Node) => void;
  onConnect: (params: Connection) => void;
  isValidConnection: IsValidConnection;
  onNodesDelete: (nodes: Node[]) => void;
  onEdgesDelete: (edges: Edge[]) => void;
  handleDragOver: (event: React.DragEvent) => void;
  handleDrop: (event: React.DragEvent) => void;
  nodeTypes: NodeTypes;
  reactFlowWrapper: RefObject<HTMLDivElement>;
  fitView?: boolean;
  showErrors?: boolean;
  workflowErrors?: WorkflowValidationError[];
}) {
  const currentTheme = useAtomValue(themeAtom);
  return (
    <Box className={styles.canvasContainer}>
      {/* Workflow error banner */}
      {showErrors && workflowErrors.length > 0 && (
        <Alert intent="danger" className={styles.workflowErrorBanner}>
          {workflowErrors[0].message}
        </Alert>
      )}
      <div
        ref={reactFlowWrapper}
        className={styles.reactFlowWrapper}
        role="application"
        aria-label="Workflow canvas - drag and drop area for building workflows"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          snapToGrid
          onNodesDelete={onNodesDelete}
          onEdgesDelete={onEdgesDelete}
          onNodeClick={onNodeClick}
          elementsSelectable
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          nodesDraggable
          nodesFocusable
          panOnScroll
          panOnDrag
          zoomOnDoubleClick
          isValidConnection={isValidConnection}
          fitView={fitView}
          proOptions={{
            hideAttribution: true,
          }}
          className={styles.reactFlow}
          deleteKeyCode={['Delete', 'Backspace']}
          connectionMode={ConnectionMode.Strict}
          connectionLineComponent={CustomConnectionLine}
          connectOnClick={false}
          colorMode={currentTheme}
        >
          <ViewportPortal>
            <Box className={styles.infiniteBackground} />
          </ViewportPortal>

          <MiniMap position="bottom-right" />
        </ReactFlow>
      </div>
    </Box>
  );
}
